<template>
  <div class="container">
    <div class="content">
      <video
        ref="retentionVideo"
        :class="`${viewsText}-text`"
        v-if="!isExternalSourceVideo"
      ></video>
      <apexchart
        :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
        v-if="chartLoaded"
        ref="realtimechart"
        height="100%"
        class="retention-chart"
        type="area"
        :options="retentionChartOptions"
        :series="retentionChartSeries"
      ></apexchart>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { addSeconds, format } from 'date-fns'
import Hls from 'hls.js'
export default {
  props: ['retentionChartSeries', 'm3u8Url', 'isExternalSourceVideo'],
  name: 'RetentionChartVideo',
  components: {
    apexchart: VueApexCharts,
  },
  data: function () {
    return {
      currentTime: 0,
      viewsText: 'x-small',
      retentionChartOptions: {
        legend: {
          show: false,
        },
        chart: {
          events: {
            mouseMove: (event, chartContext, configSet) => {
              let { dataPointIndex, config } = configSet
              if (dataPointIndex < 0) dataPointIndex = this.lastVideoFrameTime
              let currentXaxis = config.series[0].data[dataPointIndex]?.x ?? 0
              if (`${this.currentTime}` !== currentXaxis) {
                this.currentTime = parseInt(currentXaxis)
                if (!this.isExternalSourceVideo && this.$refs.retentionVideo)
                  this.$refs.retentionVideo.currentTime = parseInt(currentXaxis)
              }
            },
          },
          toolbar: false,
          type: 'area',
          height: 'auto',
          width: '100%',
          offsetX: 0,
          offsetY: 0,
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          tickAmount: 4,
          labels: {
            rotate: 0,
            formatter: (val) => {
              if (val) {
                if (val > 3600)
                  return format(
                    addSeconds(new Date('2022/01/01 00:00'), val),
                    'HH:mm:ss'
                  )
                return format(
                  addSeconds(new Date('2022/01/01 00:00'), val),
                  'mm:ss'
                )
              } else {
                return format(new Date('2022/01/01 00:00'), 'mm:ss')
              }
            },
          },
          tooltip: {
            enabled: false,
          },
        },
        tooltip: {
          theme: 'dark',
          enabled: true,
          y: {
            formatter: (val) => {
              return `${val} / ${
                ((val * 100) / this.maxValue).toFixed(0) + '%'
              }`
            },
          },
        },
        stroke: {
          curve: 'straight',
          width: 2,
        },
      },
      maxValue: 0,
      chartLoaded: false,
      hls: null,
    }
  },
  mounted() {
    this.initChart()
    if (!this.isExternalSourceVideo) this.initHls()
  },
  methods: {
    initChart() {
      const retentionValues = this.retentionChartSeries[0].data.map(
        (item) => item.y
      )
      const viewsValues = this.retentionChartSeries[1].data.map(
        (item) => item.y
      )
      this.maxValue = Math.max(...retentionValues, ...viewsValues)
      if (Math.max(...viewsValues) >= 10) {
        this.viewsText = 'small'
      }
      if (Math.max(...viewsValues) >= 100) {
        this.viewsText = 'medium'
      }
      if (Math.max(...viewsValues) >= 1000) {
        this.viewsText = 'big'
      }
      this.retentionChartOptions.yaxis = [
        {
          max:
            Math.max(this.maxValue) === 0 ? 10 : Math.max(...retentionValues),
          labels: {
            formatter: (val, i) => {
              if (val) {
                return (i * 10).toFixed(0) + '%'
              }
              return '0%'
            },
          },
        },
        {
          opposite: true,
          max: Math.max(this.maxValue) === 0 ? 10 : Math.max(...viewsValues),
          labels: {
            formatter: (val) => {
              if (val) {
                if (val > 1000000) {
                  return `${(val / 1000000).toFixed(1)}M`
                }
                if (val > 1000) {
                  return `${(val / 1000).toFixed(1)}k`
                }
                return val.toFixed(0)
              }
              return '0'
            },
          },
        },
      ]

      this.retentionChartOptions.tooltip = {
        theme: 'dark',
        enabled: true,
        y: {
          formatter: (val, i) => {
            if (i.seriesIndex === 0) {
              return `${((val * 100) / this.maxValue).toFixed(0) + '%'}`
            }
            return val
          },
        },
      }
      this.chartLoaded = true
    },
    initHls() {
      if (Hls.isSupported()) {
        this.hls = new Hls()
        this.hls.testBandwidth = false
        this.hls.startLevel = 0
        this.hls.loadSource(this.m3u8Url)
        this.hls.attachMedia(this.$refs.retentionVideo)
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.$refs.retentionVideo.pause()
        })
      } else if (this.hls.canPlayType('application/vnd.apple.mpegurl')) {
        this.hls = this.$refs.retentionVideo
        this.hls.src = this.m3u8Url
        this.hls.addEventListener('canplay', () => {
          this.$refs.retentionVideo.pause()
        })
      }
    },
  },
}
</script>
<style></style>
<style scoped>
.container {
  width: 100%;
  height: 400px;
  position: relative;
}
.content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
video {
  width: calc(100% - 124px);
  height: calc(100% - 58px);
  position: absolute;
  top: 22px;
  left: 61px;
  background-color: #000;
}
.x-small-text {
  width: calc(100% - 110px);
}
.small-text {
  width: calc(100% - 114px);
}
.medium-text {
  width: calc(100% - 120px);
}
.big-text {
  width: calc(100% - 124px);
}
.retention-chart {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
</style>
